import { ShiftTypeEnum } from '@umss/shared/utils';
import { ShiftNeed } from '../models/shift-need.model';

/**
 * Resolves the bonus amount for a shift need.
 *
 * The bonus is the extra amount the facility added on top of their configured
 * base rate when creating the shift.
 *
 * The backend now provides `bonus` directly on `ShiftNeedDto`. Falls back to
 * the legacy calculation for responses that pre-date the rate model rework.
 *
 * Returns null when no bonus is present.
 */
export function resolveShiftBonus(shiftNeed: ShiftNeed): number | null {
  // Prefer the direct field from the new API
  if (shiftNeed.bonus != null && shiftNeed.bonus > 0) {
    return shiftNeed.bonus;
  }

  // Legacy fallback: derive from rateInfo if available
  const { hourlyRate, shiftType, rateInfo } = shiftNeed;

  if (!rateInfo) return null;

  const isUrgent = shiftType === ShiftTypeEnum.Urgent;
  const configuredRate = isUrgent
    ? rateInfo.facilityUrgentHourlyRate
    : rateInfo.facilityNormalHourlyRate;

  if (configuredRate == null) return null;

  const bonus = Number(hourlyRate) - Number(configuredRate);
  return bonus > 0 ? bonus : null;
}

export interface ShiftRateBreakdown {
  /** Usage fee the shift need was created with, not the one configured today. */
  differential: number | null;
  contractorBase: number | null;
  contractorFinal: number | null;
  facilityFinal: number | null;
  contractorEstimatedPayment: number | null;
  facilityEstimatedPayment: number | null;
}

/**
 * Facility/admin view of a shift need's rates, read from the shift need itself.
 * Uses the urgent figures for urgent shifts, falling back to the normal ones
 * when no urgent rate is configured.
 */
export function resolveShiftRateBreakdown(
  shiftNeed: ShiftNeed
): ShiftRateBreakdown | null {
  const info = shiftNeed.rateInfo;
  if (!info) return null;

  const isUrgent = shiftNeed.shiftType === ShiftTypeEnum.Urgent;
  const pick = (
    urgent: number | null | undefined,
    normal: number | null | undefined
  ): number | null => (isUrgent ? (urgent ?? normal) : normal) ?? null;

  return {
    differential: shiftNeed.differentialRate ?? info.differentialRate ?? null,
    contractorBase: pick(
      info.contractorUrgentHourlyRate,
      info.contractorNormalHourlyRate
    ),
    contractorFinal: pick(
      info.contractorUrgentFinalHourlyRate,
      info.contractorNormalFinalHourlyRate
    ),
    facilityFinal: pick(
      info.facilityUrgentFinalHourlyRate,
      info.facilityNormalFinalHourlyRate
    ),
    contractorEstimatedPayment: pick(
      info.contractorUrgentEstimatedPayment,
      info.contractorNormalEstimatedPayment
    ),
    facilityEstimatedPayment: pick(
      info.facilityUrgentEstimatedPayment,
      info.facilityNormalEstimatedPayment
    )
  };
}
